import { authService } from '../services/auth';

export interface PortalAccess {
  isAdmin: boolean;
  isBillingAgent: boolean;
  /** Must pick a provider in the header before loading provider-scoped data */
  requiresProviderSelection: boolean;
  accessibleProviderIds: number[];
}

export function getAccessibleProviderIds(attr: string | undefined): number[] {
  try {
    const parsed = JSON.parse(attr || '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.map(Number).filter((id) => Number.isFinite(id) && id > 0);
  } catch {
    return [];
  }
}

export async function getPortalAccess(): Promise<PortalAccess> {
  const user = await authService.getCurrentUser();
  const isBillingAgent =
    user.attributes['custom:billing_agent_flag'] === 'true' ||
    user.attributes['custom:role'] === 'billing_agent';
  const isAdmin =
    user.attributes['custom:admin_flag'] === 'true' && !isBillingAgent;
  const accessibleProviderIds = getAccessibleProviderIds(
    user.attributes['custom:accessible_provider_ids'],
  );
  return {
    isAdmin,
    isBillingAgent,
    requiresProviderSelection: isBillingAgent,
    accessibleProviderIds,
  };
}

/** Default provider for billing agents (header selection or first assignment). */
export function defaultBillingAgentProviderId(
  selectedProvider: number | null | undefined,
  accessibleProviderIds: number[],
): number | null {
  if (selectedProvider != null) {
    return Number(selectedProvider);
  }
  return accessibleProviderIds[0] ?? null;
}

/** Provider id used for scoped API calls (header selection or provider user). */
export function resolveProviderId(
  access: PortalAccess,
  selectedProvider: number | null | undefined,
  providerIdAttr: string | undefined,
): number | null {
  // Header provider selection applies to all portal roles (admin, billing agent, provider).
  if (selectedProvider != null) {
    return Number(selectedProvider);
  }
  if (access.isAdmin) {
    return null;
  }
  if (access.isBillingAgent) {
    return defaultBillingAgentProviderId(null, access.accessibleProviderIds);
  }
  const pid = parseInt(String(providerIdAttr || ''), 10);
  return Number.isFinite(pid) ? pid : null;
}

export function canLoadProviderScopedData(
  access: PortalAccess,
  providerId: number | null,
): boolean {
  if (providerId != null) {
    return true;
  }
  return access.isAdmin;
}
